import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'
import { fichaService } from '../services/fichaService'
import { mascotaService } from '../services/mascotaService'
import { clinicaService } from '../services/clinicaService'
import { veterinarioService } from '../services/veterinarioService'
import Card, { CardHeader, CardContent } from '../components/ui/Card'
import Button from '../components/ui/Button'
import Modal, { ModalHeader, ModalContent, ModalFooter } from '../components/ui/Modal'
import Input from '../components/ui/Input'
import Textarea from '../components/ui/Textarea'
import Badge from '../components/ui/Badge'
import LoadingState from '../components/ui/LoadingState'
import EmptyState from '../components/ui/EmptyState'
import Listbox from '../components/ui/Listbox'
import Disclosure from '../components/ui/Disclosure'
import Wizard from '../components/ui/Wizard'
import { 
  FileText, 
  Eye, 
  RefreshCw, 
  AlertCircle, 
  Calendar, 
  Heart, 
  Building2, 
  User, 
  Plus,
  Edit,
  Pill,
  Activity,
  X,
  Search,
  Filter,
  Download
} from 'lucide-react'
import { clsx } from 'clsx'
import Avatar from '../components/ui/Avatar'

export default function Fichas() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { isDueno, isVeterinario, canCreateFicha } = usePermissions()
  const [fichas, setFichas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [filtros, setFiltros] = useState({
    id_mascota: '',
    id_clinica: '',
    estado_caso: ''
  })
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 15,
    total: 0,
    last_page: 1
  })
  const [mascotas, setMascotas] = useState([])
  const [clinicas, setClinicas] = useState([])
  const [veterinarios, setVeterinarios] = useState([])
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showTratamientoModal, setShowTratamientoModal] = useState(false)
  const [showSeguimientoModal, setShowSeguimientoModal] = useState(false)
  const [selectedFicha, setSelectedFicha] = useState(null)
  const [tratamientos, setTratamientos] = useState([])
  const [seguimientos, setSeguimientos] = useState([])
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [downloadingPDF, setDownloadingPDF] = useState(null) // ID de la ficha que se está descargando

  // Form states
  const [formData, setFormData] = useState({
    id_mascota: '',
    id_clinica: '',
    id_veterinario: '',
    fecha_consulta: new Date().toISOString().split('T')[0],
    motivo_consulta: '',
    anamnesis_sanitaria: '',
    anamnesis_ambiental: '',
    examen_fisico_general: '',
    examen_fisico_particular: '',
    diagnostico_presuntivo: '',
    diagnostico_definitivo: '',
    pronostico: '',
    tratamiento_especifico: '',
    observaciones: '',
    estado_caso: 'En tratamiento',
    costo_consulta: ''
  })
  const [tratamientoData, setTratamientoData] = useState({
    medicamento: '',
    dosis: '',
    frecuencia: '',
    duracion: '',
    via_administracion: '',
    tipo_tratamiento: '',
    observaciones: ''
  })
  const [seguimientoData, setSeguimientoData] = useState({
    fecha_seguimiento: new Date().toISOString().split('T')[0],
    observaciones: '',
    estado_paciente: '',
    proxima_cita: ''
  })
  const [formErrors, setFormErrors] = useState({})
  const [saving, setSaving] = useState(false)
  const [wizardStep, setWizardStep] = useState(0)
  const [wizardSteps, setWizardSteps] = useState([
    { id: 1, name: 'Información Básica', label: 'Básica', description: 'Mascota, clínica y veterinario', completed: false },
    { id: 2, name: 'Anamnesis', label: 'Anamnesis', description: 'Historial sanitario', completed: false },
    { id: 3, name: 'Examen Físico', label: 'Examen', description: 'Examen físico general', completed: false },
    { id: 4, name: 'Diagnóstico', label: 'Diagnóstico', description: 'Diagnóstico y pronóstico', completed: false },
    { id: 5, name: 'Tratamiento', label: 'Tratamiento', description: 'Tratamiento y costos', completed: false },
  ])

  useEffect(() => {
    fetchFichas()
    fetchMascotas()
    fetchClinicas()
  }, [pagination.current_page])

  useEffect(() => {
    if (formData.id_clinica) {
      fetchVeterinariosByClinica(formData.id_clinica)
    } else {
      setVeterinarios([])
      setFormData(prev => ({ ...prev, id_veterinario: '' }))
    }
  }, [formData.id_clinica])

  const fetchFichas = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const params = {
        per_page: pagination.per_page,
        page: pagination.current_page,
        ...(filtros.id_mascota && { id_mascota: filtros.id_mascota }),
        ...(filtros.id_clinica && { id_clinica: filtros.id_clinica }),
        ...(filtros.estado_caso && { estado_caso: filtros.estado_caso })
      }

      // Si es veterinario, filtrar solo sus fichas
      if (isVeterinario) {
        params.id_veterinario = user?.veterinarios?.[0]?.id_veterinario || user?.veterinarios?.[0]?.idVeterinario
      }

      const response = await fichaService.getAll(params)
      
      if (response.success) {
        if (response.data.data) {
          setFichas(response.data.data)
          setPagination({
            current_page: response.data.current_page || 1,
            per_page: response.data.per_page || 15,
            total: response.data.total || 0,
            last_page: response.data.last_page || 1
          })
        } else {
          setFichas(Array.isArray(response.data) ? response.data : [])
        }
      } else {
        setError(response.error || 'Error al cargar fichas clínicas')
      }
    } catch (err) {
      setError(err.message || 'Error de conexión')
      console.error('Error fetching fichas:', err)
    } finally {
      setLoading(false)
    }
  }

  const fetchMascotas = async () => {
    try {
      // Si es DUEÑO, solo obtener sus mascotas
      const params = { per_page: 100 }
      if (isDueno) {
        params.id_dueño = user?.idUsuario || user?.id_usuario
      }
      const response = await mascotaService.getAll(params)
      if (response.success) {
        setMascotas(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching mascotas:', err)
    }
  }

  const fetchClinicas = async () => {
    try {
      // Si es veterinario, solo obtener su clínica
      const params = { per_page: 100 }
      if (isVeterinario && user?.veterinarios?.[0]?.id_clinica) {
        params.id_clinica = user.veterinarios[0].id_clinica
      }
      const response = await clinicaService.getAll(params)
      if (response.success) {
        const clinicasData = response.data.data || response.data || []
        setClinicas(clinicasData)
        // Si es veterinario y solo hay una clínica, establecerla automáticamente en filtros
        if (isVeterinario && clinicasData.length === 1 && !filtros.id_clinica) {
          setFiltros(prev => ({ ...prev, id_clinica: clinicasData[0].id_clinica }))
        }
      }
    } catch (err) {
      console.error('Error fetching clínicas:', err)
    }
  }

  const fetchVeterinariosByClinica = async (idClinica) => {
    try {
      const response = await veterinarioService.getAll({ id_clinica: idClinica, per_page: 100 })
      if (response.success) {
        setVeterinarios(response.data.data || response.data || [])
      }
    } catch (err) {
      console.error('Error fetching veterinarios:', err)
    }
  }

  const handleViewDetail = async (ficha) => {
    try {
      setSelectedFicha(ficha)
      setLoadingDetail(true)
      setShowDetailModal(true)

      const [fichaResponse, tratamientosResponse, seguimientosResponse] = await Promise.all([
        fichaService.getById(ficha.id_ficha || ficha.idFicha),
        fichaService.getTratamientos(ficha.id_ficha || ficha.idFicha),
        fichaService.getSeguimientos(ficha.id_ficha || ficha.idFicha)
      ])

      if (fichaResponse.success) {
        setSelectedFicha(fichaResponse.data)
      }
      if (tratamientosResponse.success) {
        setTratamientos(tratamientosResponse.data || [])
      }
      if (seguimientosResponse.success) {
        setSeguimientos(seguimientosResponse.data || [])
      }
    } catch (err) {
      console.error('Error loading ficha detail:', err)
    } finally {
      setLoadingDetail(false)
    }
  }

  const handleCreateFicha = async () => {
    try {
      setSaving(true)
      setFormErrors({})
      setError(null)

      const response = await fichaService.create(formData)
      
      if (response.success) {
        setShowCreateModal(false)
        resetForm()
        // Resetear paginación y refrescar después de un breve delay
        setPagination(prev => ({ ...prev, current_page: 1 }))
        setTimeout(() => {
          fetchFichas()
        }, 500)
      } else {
        if (response.error && response.error.includes('permisos')) {
          setError(response.error)
        } else {
          setFormErrors(response.details || {})
          if (response.error) {
            setError(response.error)
          }
        }
      }
    } catch (err) {
      console.error('Error creating ficha:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Error al crear ficha'
      setError(errorMessage)
      if (err.response?.data?.details) {
        setFormErrors(err.response.data.details)
      }
    } finally {
      setSaving(false)
    }
  }

  const handleAddTratamiento = async () => {
    try {
      setSaving(true)
      setError(null)
      
      const response = await fichaService.addTratamiento(
        selectedFicha.id_ficha || selectedFicha.idFicha,
        tratamientoData
      )
      
      if (response.success) {
        setShowTratamientoModal(false)
        setTratamientoData({
          medicamento: '',
          dosis: '',
          frecuencia: '',
          duracion: '',
          via_administracion: '',
          tipo_tratamiento: '',
          observaciones: ''
        })
        
        // Recargar tratamientos
        const tratResponse = await fichaService.getTratamientos(selectedFicha.id_ficha || selectedFicha.idFicha)
        if (tratResponse.success) {
          setTratamientos(tratResponse.data || [])
        }
      } else {
        setError(response.error || 'Error al agregar tratamiento')
      }
    } catch (err) {
      console.error('Error adding tratamiento:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Error al agregar tratamiento'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const handleAddSeguimiento = async () => {
    try {
      setSaving(true)
      setError(null)
      
      const response = await fichaService.addSeguimiento(
        selectedFicha.id_ficha || selectedFicha.idFicha,
        seguimientoData
      )
      
      if (response.success) {
        setShowSeguimientoModal(false)
        setSeguimientoData({
          fecha_seguimiento: new Date().toISOString().split('T')[0],
          observaciones: '',
          estado_paciente: '',
          proxima_cita: ''
        })
        
        // Recargar seguimientos
        const segResponse = await fichaService.getSeguimientos(selectedFicha.id_ficha || selectedFicha.idFicha)
        if (segResponse.success) {
          setSeguimientos(segResponse.data || [])
        }
      } else {
        setError(response.error || 'Error al agregar seguimiento')
      }
    } catch (err) {
      console.error('Error adding seguimiento:', err)
      const errorMessage = err.response?.data?.error || err.message || 'Error al agregar seguimiento'
      setError(errorMessage)
    } finally {
      setSaving(false)
    }
  }

  const resetForm = () => {
    setFormData({
      id_mascota: '',
      id_clinica: '',
      id_veterinario: '',
      fecha_consulta: new Date().toISOString().split('T')[0],
      motivo_consulta: '',
      anamnesis_sanitaria: '',
      anamnesis_ambiental: '',
      examen_fisico_general: '',
      examen_fisico_particular: '',
      diagnostico_presuntivo: '',
      diagnostico_definitivo: '',
      pronostico: '',
      tratamiento_especifico: '',
      observaciones: '',
      estado_caso: 'En tratamiento',
      costo_consulta: ''
    })
    setFormErrors({})
    setWizardStep(0)
    setWizardSteps([
      { id: 1, name: 'Información Básica', label: 'Básica', description: 'Mascota, clínica y veterinario', completed: false },
      { id: 2, name: 'Anamnesis', label: 'Anamnesis', description: 'Historial sanitario', completed: false },
      { id: 3, name: 'Examen Físico', label: 'Examen', description: 'Examen físico general', completed: false },
      { id: 4, name: 'Diagnóstico', label: 'Diagnóstico', description: 'Diagnóstico y pronóstico', completed: false },
      { id: 5, name: 'Tratamiento', label: 'Tratamiento', description: 'Tratamiento y costos', completed: false },
    ])
  }
  
  const validateStep = (step) => {
    const errors = {}
    
    switch(step) {
      case 0:
        if (!formData.id_mascota) errors.id_mascota = ['La mascota es requerida']
        if (!formData.id_clinica) errors.id_clinica = ['La clínica es requerida']
        if (!formData.id_veterinario) errors.id_veterinario = ['El veterinario es requerido']
        if (!formData.fecha_consulta) errors.fecha_consulta = ['La fecha de consulta es requerida']
        if (!formData.motivo_consulta) errors.motivo_consulta = ['El motivo de consulta es requerido']
        break
      // Los demás pasos son opcionales
    }
    
    return errors
  }
  
  const handleNext = () => {
    const stepErrors = validateStep(wizardStep)
    if (Object.keys(stepErrors).length > 0) {
      setFormErrors(stepErrors)
      return
    }
    setFormErrors({})
    
    // Marcar paso como completado
    setWizardSteps(prevSteps => prevSteps.map((step, idx) => ({
      ...step,
      completed: idx < wizardStep + 1
    })))
    
    if (wizardStep < wizardSteps.length - 1) {
      setWizardStep(wizardStep + 1)
    }
  }
  
  const handlePrevious = () => {
    if (wizardStep > 0) {
      setWizardStep(wizardStep - 1)
      setFormErrors({})
    }
  }

  const getEstadoBadge = (estado) => {
    const estados = {
      'En tratamiento': 'warning',
      'Curado': 'success',
      'En observación': 'info',
      'Cerrado': 'default',
      'Abierto': 'primary'
    }
    return <Badge variant={estados[estado] || 'default'}>{estado || 'Sin estado'}</Badge>
  }

  // SUPERADMIN puede hacer todo

  if (loading && fichas.length === 0) {
    return <LoadingState message="Cargando fichas clínicas..." />
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4 bg-white/95 border border-pastel-pink rounded-3xl p-4 sm:p-5 shadow-soft">
        <div className="min-w-0 flex-1">
          <p className="text-xs uppercase tracking-wider text-pastel-lavender font-semibold">Medical Records</p>
          <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-pastel-ink via-primary-600 to-pastel-ink bg-clip-text text-transparent">
            {isDueno ? 'Fichas de Mis Mascotas' : 'Fichas Clínicas'}
          </h1>
          <p className="mt-1 text-xs sm:text-sm text-slate-500 dark:text-gray-500">
            {isDueno 
              ? 'Historial médico de tus mascotas'
              : 'Historial médico de las mascotas'}
          </p>
        </div>
        {canCreateFicha && (
          <Button onClick={() => setShowCreateModal(true)} className="w-full sm:w-auto">
            <Plus className="w-4 h-4 mr-2" />
            Nueva Ficha
          </Button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-2xl flex items-center">
          <AlertCircle className="w-5 h-5 mr-2" />
          {error}
        </div>
      )}

      {/* Filtros */}
      <Disclosure 
        title="Filtros de Búsqueda"
        defaultOpen={filtros.id_mascota || filtros.id_clinica || filtros.estado_caso}
        className="bg-white/95 dark:bg-gray-800 rounded-3xl border border-pastel-pink dark:border-gray-700 shadow-soft"
      >
        <div className="space-y-4 pt-2">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Listbox
              label="Mascota"
              options={[
                { value: '', label: 'Todas' },
                ...mascotas.map((mascota) => ({
                  value: mascota.id_mascota || mascota.idMascota,
                  label: `${mascota.nombre} (${mascota.especie})`
                }))
              ]}
              value={filtros.id_mascota}
              onChange={(value) => setFiltros({ ...filtros, id_mascota: value })}
            />
            <Listbox
              label="Clínica"
              options={[
                { value: '', label: 'Todas' },
                ...clinicas.map((clinica) => ({
                  value: clinica.id_clinica || clinica.idClinica,
                  label: clinica.nombre
                }))
              ]}
              value={filtros.id_clinica}
              onChange={(value) => setFiltros({ ...filtros, id_clinica: value })}
            />
            <Listbox
              label="Estado"
              options={[
                { value: '', label: 'Todos' },
                { value: 'En tratamiento', label: 'En tratamiento' },
                { value: 'Curado', label: 'Curado' },
                { value: 'En observación', label: 'En observación' },
                { value: 'Cerrado', label: 'Cerrado' }
              ]}
              value={filtros.estado_caso}
              onChange={(value) => setFiltros({ ...filtros, estado_caso: value })}
            />
            <div className="flex items-end">
              <Button onClick={fetchFichas} variant="secondary" className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Actualizar
              </Button>
            </div>
          </div>
        </div>
      </Disclosure>

      {/* Tabla */}
      {fichas.length === 0 ? (
        <EmptyState
          icon={FileText}
          title="No hay fichas clínicas"
          description="No se encontraron fichas clínicas con los filtros seleccionados."
        />
      ) : (
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-pastel-pink dark:divide-gray-700">
              <thead className="bg-pastel-cream dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Número</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Mascota</th>
                  <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Clínica</th>
                  <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Veterinario</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Fecha</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Estado</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-slate-600 dark:text-gray-300 uppercase tracking-wider">Acciones</th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-pastel-pink dark:divide-gray-700">
                {fichas.map((ficha) => (
                  <tr key={ficha.id_ficha || ficha.idFicha} className="hover:bg-pastel-cream/60 dark:hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {ficha.numero_ficha || ficha.numeroFicha || `FC-${ficha.id_ficha || ficha.idFicha}`}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {ficha.mascota ? (
                        <div className="flex items-center space-x-2">
                          <Heart className="w-4 h-4 text-pastel-teal" />
                          <div>
                            <div className="font-medium">{ficha.mascota.nombre}</div>
                            <div className="text-xs text-gray-600 dark:text-gray-500 md:hidden">{ficha.mascota.especie}</div>
                          </div>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {ficha.clinica ? (
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-gray-500" />
                          <span>{ficha.clinica.nombre || ficha.clinica.name}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {ficha.veterinario && ficha.veterinario.usuario 
                        ? (
                          <div className="flex items-center space-x-3">
                            <Avatar user={ficha.veterinario.usuario} size="sm" />
                            <span>{`${ficha.veterinario.usuario.nombre} ${ficha.veterinario.usuario.apellido || ''}`.trim()}</span>
                          </div>
                        )
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 dark:text-gray-300">
                      {ficha.fecha_consulta || ficha.fechaConsulta
                        ? new Date(ficha.fecha_consulta || ficha.fechaConsulta).toLocaleDateString('es-ES')
                        : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {getEstadoBadge(ficha.estado_caso || ficha.estadoCaso)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center space-x-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleViewDetail(ficha)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Ver
                        </Button>
                        {isDueno && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={downloadingPDF === (ficha.id_ficha || ficha.idFicha)}
                            onClick={async () => {
                              const fichaId = ficha.id_ficha || ficha.idFicha
                              try {
                                setDownloadingPDF(fichaId)
                                setError(null)
                                await fichaService.descargarPDF(fichaId)
                              } catch (err) {
                                console.error('Error al descargar PDF:', err)
                                const errorMessage = err.response?.data?.error || err.message || 'Error al descargar el PDF de la ficha clínica'
                                setError(errorMessage)
                              } finally {
                                setDownloadingPDF(null)
                              }
                            }}
                            title="Descargar PDF"
                          >
                            {downloadingPDF === (ficha.id_ficha || ficha.idFicha) ? (
                              <RefreshCw className="w-4 h-4 animate-spin" />
                            ) : (
                              <Download className="w-4 h-4" />
                            )}
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Paginación */}
          {pagination.last_page > 1 && (
            <div className="px-6 py-4 border-t border-pastel-pink dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total} resultados
              </div>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.current_page === 1}
                  onClick={() => setPagination({ ...pagination, current_page: pagination.current_page - 1 })}
                >
                  Anterior
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={pagination.current_page === pagination.last_page}
                  onClick={() => setPagination({ ...pagination, current_page: pagination.current_page + 1 })}
                >
                  Siguiente
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Modal Crear Ficha con Wizard */}
      <Modal isOpen={showCreateModal} onClose={() => { setShowCreateModal(false); resetForm(); }} size="xl">
        <ModalHeader onClose={() => { setShowCreateModal(false); resetForm(); }}>
          Nueva Ficha Clínica
        </ModalHeader>
        <ModalContent>
          <div className="space-y-6">
            {/* Wizard Steps */}
            <Wizard 
              steps={wizardSteps} 
              currentStep={wizardStep}
              onStepClick={(step) => {
                // Solo permitir ir a pasos completados o el siguiente
                if (step <= wizardStep + 1) {
                  const stepErrors = validateStep(step - 1)
                  if (Object.keys(stepErrors).length === 0 || step <= wizardStep) {
                    setWizardStep(step)
                    setFormErrors({})
                  } else {
                    setFormErrors(stepErrors)
                  }
                }
              }}
            />

            {/* Step Content */}
            <div className="mt-8 min-h-[400px]">
              {wizardStep === 0 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Información Básica</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Listbox
                      label="Mascota"
                      required
                      options={[
                        { value: '', label: 'Seleccionar mascota' },
                        ...mascotas.map((mascota) => ({
                          value: mascota.id_mascota || mascota.idMascota,
                          label: `${mascota.nombre} (${mascota.especie})`
                        }))
                      ]}
                      value={formData.id_mascota}
                      onChange={(value) => setFormData({ ...formData, id_mascota: value })}
                      error={formErrors.id_mascota?.[0]}
                      placeholder="Seleccionar mascota..."
                    />
                    <Listbox
                      label="Clínica"
                      required
                      options={[
                        { value: '', label: 'Seleccionar clínica' },
                        ...clinicas.map((clinica) => ({
                          value: clinica.id_clinica || clinica.idClinica,
                          label: clinica.nombre
                        }))
                      ]}
                      value={formData.id_clinica}
                      onChange={(value) => setFormData({ ...formData, id_clinica: value })}
                      error={formErrors.id_clinica?.[0]}
                      placeholder="Seleccionar clínica..."
                    />
                    <Listbox
                      label="Veterinario"
                      required
                      options={[
                        { value: '', label: 'Seleccionar veterinario' },
                        ...veterinarios.map((vet) => ({
                          value: vet.id_veterinario || vet.idVeterinario,
                          label: vet.usuario ? `${vet.usuario.nombre} ${vet.usuario.apellido || ''}`.trim() : `Veterinario #${vet.id_veterinario}`
                        }))
                      ]}
                      value={formData.id_veterinario}
                      onChange={(value) => setFormData({ ...formData, id_veterinario: value })}
                      disabled={!formData.id_clinica}
                      error={formErrors.id_veterinario?.[0]}
                      placeholder="Seleccionar veterinario..."
                    />
                    <Input
                      label="Fecha Consulta"
                      type="date"
                      required
                      value={formData.fecha_consulta}
                      onChange={(e) => setFormData({ ...formData, fecha_consulta: e.target.value })}
                      error={formErrors.fecha_consulta?.[0]}
                    />
                    <div className="md:col-span-2">
                      <Textarea
                        label="Motivo de Consulta"
                        required
                        value={formData.motivo_consulta}
                        onChange={(e) => setFormData({ ...formData, motivo_consulta: e.target.value })}
                        rows={4}
                        error={formErrors.motivo_consulta?.[0]}
                      />
                    </div>
                  </div>
                </div>
              )}

              {wizardStep === 1 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Anamnesis</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <Textarea
                      label="Anamnesis Sanitaria"
                      value={formData.anamnesis_sanitaria}
                      onChange={(e) => setFormData({ ...formData, anamnesis_sanitaria: e.target.value })}
                      rows={5}
                      placeholder="Historial sanitario de la mascota, enfermedades previas, vacunaciones, etc."
                    />
                    <Textarea
                      label="Anamnesis Ambiental"
                      value={formData.anamnesis_ambiental}
                      onChange={(e) => setFormData({ ...formData, anamnesis_ambiental: e.target.value })}
                      rows={5}
                      placeholder="Condiciones del ambiente donde vive la mascota, exposición a otros animales, etc."
                    />
                  </div>
                </div>
              )}

              {wizardStep === 2 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Examen Físico</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <Textarea
                      label="Examen Físico General"
                      value={formData.examen_fisico_general}
                      onChange={(e) => setFormData({ ...formData, examen_fisico_general: e.target.value })}
                      rows={5}
                      placeholder="Estado general, temperatura, pulso, respiración, hidratación, etc."
                    />
                    <Textarea
                      label="Examen Físico Particular"
                      value={formData.examen_fisico_particular}
                      onChange={(e) => setFormData({ ...formData, examen_fisico_particular: e.target.value })}
                      rows={5}
                      placeholder="Examen específico del sistema afectado, signos clínicos observados, etc."
                    />
                  </div>
                </div>
              )}

              {wizardStep === 3 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Diagnóstico</h3>
                  <div className="grid grid-cols-1 gap-4">
                    <Textarea
                      label="Diagnóstico Presuntivo"
                      value={formData.diagnostico_presuntivo}
                      onChange={(e) => setFormData({ ...formData, diagnostico_presuntivo: e.target.value })}
                      rows={4}
                      placeholder="Diagnóstico inicial basado en síntomas y examen físico"
                    />
                    <Textarea
                      label="Diagnóstico Definitivo"
                      value={formData.diagnostico_definitivo}
                      onChange={(e) => setFormData({ ...formData, diagnostico_definitivo: e.target.value })}
                      rows={4}
                      placeholder="Diagnóstico confirmado después de exámenes complementarios"
                    />
                    <Textarea
                      label="Pronóstico"
                      value={formData.pronostico}
                      onChange={(e) => setFormData({ ...formData, pronostico: e.target.value })}
                      rows={3}
                      placeholder="Pronóstico del caso (favorable, reservado, desfavorable)"
                    />
                  </div>
                </div>
              )}

              {wizardStep === 4 && (
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Tratamiento y Costos</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Textarea
                        label="Tratamiento Específico"
                        value={formData.tratamiento_especifico}
                        onChange={(e) => setFormData({ ...formData, tratamiento_especifico: e.target.value })}
                        rows={4}
                        placeholder="Descripción del tratamiento prescrito"
                      />
                    </div>
                    <Listbox
                      label="Estado del Caso"
                      options={[
                        { value: 'En tratamiento', label: 'En tratamiento' },
                        { value: 'Curado', label: 'Curado' },
                        { value: 'En observación', label: 'En observación' },
                        { value: 'Cerrado', label: 'Cerrado' }
                      ]}
                      value={formData.estado_caso}
                      onChange={(value) => setFormData({ ...formData, estado_caso: value })}
                    />
                    <Input
                      label="Costo Consulta"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.costo_consulta}
                      onChange={(e) => setFormData({ ...formData, costo_consulta: e.target.value })}
                      placeholder="0.00"
                    />
                    <div className="md:col-span-2">
                      <Textarea
                        label="Observaciones"
                        value={formData.observaciones}
                        onChange={(e) => setFormData({ ...formData, observaciones: e.target.value })}
                        rows={3}
                        placeholder="Observaciones adicionales sobre el caso"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </ModalContent>
        <ModalFooter>
          <div className="flex justify-between w-full">
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => { setShowCreateModal(false); resetForm(); }}
              disabled={saving}
            >
              Cancelar
            </Button>
            <div className="flex space-x-3">
              {wizardStep > 0 && (
                <Button 
                  type="button" 
                  variant="secondary" 
                  onClick={handlePrevious}
                  disabled={saving}
                >
                  Anterior
                </Button>
              )}
              {wizardStep < wizardSteps.length - 1 ? (
                <Button 
                  type="button" 
                  onClick={handleNext}
                  disabled={saving}
                >
                  Siguiente
                </Button>
              ) : (
                <Button 
                  type="button" 
                  onClick={handleCreateFicha} 
                  loading={saving}
                  disabled={saving}
                >
                  Crear Ficha
                </Button>
              )}
            </div>
          </div>
        </ModalFooter>
      </Modal>

      {/* Modal Detalle Ficha */}
      <Modal isOpen={showDetailModal} onClose={() => setShowDetailModal(false)} size="full">
        <ModalHeader onClose={() => setShowDetailModal(false)}>
          {selectedFicha && (
            <div className="flex items-center justify-between w-full">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
                  Ficha Clínica {selectedFicha.numero_ficha || selectedFicha.numeroFicha || `FC-${selectedFicha.id_ficha || selectedFicha.idFicha}`}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-500 mt-1">
                  {selectedFicha.mascota?.nombre && `${selectedFicha.mascota.nombre} - ${selectedFicha.mascota.especie}`}
                </p>
              </div>
              {canCreateFicha && selectedFicha && (
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowTratamientoModal(true)
                    }}
                  >
                    <Pill className="w-4 h-4 mr-1" />
                    Agregar Tratamiento
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setShowSeguimientoModal(true)
                    }}
                  >
                    <Activity className="w-4 h-4 mr-1" />
                    Agregar Seguimiento
                  </Button>
                </div>
              )}
            </div>
          )}
        </ModalHeader>
        <ModalContent>
          {loadingDetail ? (
            <LoadingState message="Cargando detalles..." />
          ) : selectedFicha ? (
            <div className="space-y-6">
              {/* Información General */}
              <Card>
                <CardHeader>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Información General</h4>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Mascota</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {selectedFicha.mascota?.nombre || '-'} ({selectedFicha.mascota?.especie || '-'})
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Clínica</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {selectedFicha.clinica?.nombre || '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Veterinario</p>
                      {selectedFicha.veterinario && selectedFicha.veterinario.usuario ? (
                        <div className="mt-1 flex items-center space-x-2">
                          <Avatar user={selectedFicha.veterinario.usuario} size="sm" />
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {`${selectedFicha.veterinario.usuario.nombre} ${selectedFicha.veterinario.usuario.apellido || ''}`.trim()}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">-</p>
                      )}
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Fecha Consulta</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        {selectedFicha.fecha_consulta || selectedFicha.fechaConsulta
                          ? new Date(selectedFicha.fecha_consulta || selectedFicha.fechaConsulta).toLocaleDateString('es-ES')
                          : '-'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Estado</p>
                      <p className="mt-1">{getEstadoBadge(selectedFicha.estado_caso || selectedFicha.estadoCaso)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 dark:text-gray-500">Costo</p>
                      <p className="mt-1 text-sm font-medium text-gray-900 dark:text-white">
                        ${selectedFicha.costo_consulta || selectedFicha.costoConsulta || '0.00'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Motivo de Consulta */}
              {selectedFicha.motivo_consulta && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Motivo de Consulta</h4>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedFicha.motivo_consulta}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Anamnesis */}
              {(selectedFicha.anamnesis_sanitaria || selectedFicha.anamnesis_ambiental) && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Anamnesis</h4>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedFicha.anamnesis_sanitaria && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Sanitaria</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.anamnesis_sanitaria}
                          </p>
                        </div>
                      )}
                      {selectedFicha.anamnesis_ambiental && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Ambiental</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.anamnesis_ambiental}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Examen Físico */}
              {(selectedFicha.examen_fisico_general || selectedFicha.examen_fisico_particular) && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Examen Físico</h4>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedFicha.examen_fisico_general && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">General</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.examen_fisico_general}
                          </p>
                        </div>
                      )}
                      {selectedFicha.examen_fisico_particular && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Particular</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.examen_fisico_particular}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Diagnóstico */}
              {(selectedFicha.diagnostico_presuntivo || selectedFicha.diagnostico_definitivo || selectedFicha.pronostico) && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Diagnóstico</h4>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {selectedFicha.diagnostico_presuntivo && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Presuntivo</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.diagnostico_presuntivo}
                          </p>
                        </div>
                      )}
                      {selectedFicha.diagnostico_definitivo && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Definitivo</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.diagnostico_definitivo}
                          </p>
                        </div>
                      )}
                      {selectedFicha.pronostico && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Pronóstico</p>
                          <p className="text-sm text-gray-700 dark:text-gray-500 whitespace-pre-wrap">
                            {selectedFicha.pronostico}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Tratamiento Específico */}
              {selectedFicha.tratamiento_especifico && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tratamiento Específico</h4>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedFicha.tratamiento_especifico}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Tratamientos */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Tratamientos</h4>
                    <Badge variant="info">{tratamientos.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {tratamientos.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-500">No hay tratamientos registrados</p>
                  ) : (
                    <div className="space-y-4">
                      {tratamientos.map((tratamiento) => (
                        <div key={tratamiento.id_tratamiento || tratamiento.idTratamiento} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Pill className="w-5 h-5 text-primary-600" />
                              <h5 className="font-semibold text-gray-900 dark:text-white">{tratamiento.medicamento}</h5>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                            <div>
                              <p className="text-gray-600 dark:text-gray-500">Dosis</p>
                              <p className="font-medium text-gray-900 dark:text-white">{tratamiento.dosis}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-500">Frecuencia</p>
                              <p className="font-medium text-gray-900 dark:text-white">{tratamiento.frecuencia}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-500">Duración</p>
                              <p className="font-medium text-gray-900 dark:text-white">{tratamiento.duracion}</p>
                            </div>
                            <div>
                              <p className="text-gray-600 dark:text-gray-500">Vía</p>
                              <p className="font-medium text-gray-900 dark:text-white">{tratamiento.via_administracion || '-'}</p>
                            </div>
                          </div>
                          {tratamiento.observaciones && (
                            <div className="mt-2">
                              <p className="text-sm text-gray-700 dark:text-gray-500">{tratamiento.observaciones}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seguimientos */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Seguimientos</h4>
                    <Badge variant="info">{seguimientos.length}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {seguimientos.length === 0 ? (
                    <p className="text-sm text-gray-600 dark:text-gray-500">No hay seguimientos registrados</p>
                  ) : (
                    <div className="space-y-4">
                      {seguimientos.map((seguimiento) => (
                        <div key={seguimiento.id_seguimiento || seguimiento.idSeguimiento} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center space-x-2">
                              <Activity className="w-5 h-5 text-green-700" />
                              <div>
                                <p className="font-semibold text-gray-900 dark:text-white">
                                  {seguimiento.fecha_seguimiento || seguimiento.fechaSeguimiento
                                    ? new Date(seguimiento.fecha_seguimiento || seguimiento.fechaSeguimiento).toLocaleDateString('es-ES')
                                    : '-'}
                                </p>
                                {seguimiento.estado_paciente && (
                                  <Badge variant="success" size="sm" className="mt-1">
                                    {seguimiento.estado_paciente}
                                  </Badge>
                                )}
                              </div>
                            </div>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap mb-2">
                            {seguimiento.observaciones}
                          </p>
                          {seguimiento.proxima_cita && (
                            <p className="text-xs text-gray-600 dark:text-gray-500">
                              Próxima cita: {new Date(seguimiento.proxima_cita).toLocaleDateString('es-ES')}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Observaciones */}
              {selectedFicha.observaciones && (
                <Card>
                  <CardHeader>
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white">Observaciones</h4>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {selectedFicha.observaciones}
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </ModalContent>
        <ModalFooter>
          <div className="flex items-center justify-between w-full">
            {isDueno && selectedFicha && (
              <Button
                variant="primary"
                disabled={downloadingPDF === (selectedFicha.id_ficha || selectedFicha.idFicha)}
                onClick={async () => {
                  const fichaId = selectedFicha.id_ficha || selectedFicha.idFicha
                  try {
                    setDownloadingPDF(fichaId)
                    setError(null)
                    await fichaService.descargarPDF(fichaId)
                  } catch (err) {
                    console.error('Error al descargar PDF:', err)
                    const errorMessage = err.response?.data?.error || err.message || 'Error al descargar el PDF de la ficha clínica'
                    setError(errorMessage)
                  } finally {
                    setDownloadingPDF(null)
                  }
                }}
              >
                {downloadingPDF === (selectedFicha.id_ficha || selectedFicha.idFicha) ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Descargando...
                  </>
                ) : (
                  <>
                    <Download className="w-4 h-4 mr-2" />
                    Descargar PDF
                  </>
                )}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
              Cerrar
            </Button>
          </div>
        </ModalFooter>
      </Modal>

      {/* Modal Agregar Tratamiento */}
      <Modal isOpen={showTratamientoModal} onClose={() => setShowTratamientoModal(false)} size="lg">
        <ModalHeader onClose={() => setShowTratamientoModal(false)}>
          Agregar Tratamiento
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Medicamento"
                value={tratamientoData.medicamento}
                onChange={(e) => setTratamientoData({ ...tratamientoData, medicamento: e.target.value })}
                required
              />
              <Input
                label="Dosis"
                value={tratamientoData.dosis}
                onChange={(e) => setTratamientoData({ ...tratamientoData, dosis: e.target.value })}
                required
              />
              <Input
                label="Frecuencia"
                value={tratamientoData.frecuencia}
                onChange={(e) => setTratamientoData({ ...tratamientoData, frecuencia: e.target.value })}
                required
              />
              <Input
                label="Duración"
                value={tratamientoData.duracion}
                onChange={(e) => setTratamientoData({ ...tratamientoData, duracion: e.target.value })}
                required
              />
              <Input
                label="Vía de Administración"
                value={tratamientoData.via_administracion}
                onChange={(e) => setTratamientoData({ ...tratamientoData, via_administracion: e.target.value })}
              />
              <Input
                label="Tipo de Tratamiento"
                value={tratamientoData.tipo_tratamiento}
                onChange={(e) => setTratamientoData({ ...tratamientoData, tipo_tratamiento: e.target.value })}
              />
            </div>
            <Textarea
              label="Observaciones"
              value={tratamientoData.observaciones}
              onChange={(e) => setTratamientoData({ ...tratamientoData, observaciones: e.target.value })}
              rows={3}
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowTratamientoModal(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleAddTratamiento} loading={saving} disabled={saving}>
            Agregar Tratamiento
          </Button>
        </ModalFooter>
      </Modal>

      {/* Modal Agregar Seguimiento */}
      <Modal isOpen={showSeguimientoModal} onClose={() => setShowSeguimientoModal(false)} size="lg">
        <ModalHeader onClose={() => setShowSeguimientoModal(false)}>
          Agregar Seguimiento
        </ModalHeader>
        <ModalContent>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Fecha Seguimiento"
                type="date"
                value={seguimientoData.fecha_seguimiento}
                onChange={(e) => setSeguimientoData({ ...seguimientoData, fecha_seguimiento: e.target.value })}
                required
              />
              <Input
                label="Estado del Paciente"
                value={seguimientoData.estado_paciente}
                onChange={(e) => setSeguimientoData({ ...seguimientoData, estado_paciente: e.target.value })}
              />
              <Input
                label="Próxima Cita"
                type="date"
                value={seguimientoData.proxima_cita}
                onChange={(e) => setSeguimientoData({ ...seguimientoData, proxima_cita: e.target.value })}
                className="md:col-span-2"
              />
            </div>
            <Textarea
              label="Observaciones"
              value={seguimientoData.observaciones}
              onChange={(e) => setSeguimientoData({ ...seguimientoData, observaciones: e.target.value })}
              rows={4}
              required
            />
          </div>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondary" onClick={() => setShowSeguimientoModal(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button onClick={handleAddSeguimiento} loading={saving} disabled={saving}>
            Agregar Seguimiento
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  )
}
